import { OverviewIcon } from "components/atoms/Icon";
import { Header } from "components/atoms/Typography";
import { usePermissions } from "hooks/usePermissions";
import { UserPermissions } from "utils/enums";
import { BuildingSectionProps, BuildingPropertyField } from "../BuildingConstruction.constants";
import BuildingSectionBody from "./BuildingSectionBody";

export const financialProperties: BuildingPropertyField[] = [
    {
        type: "number",
        key: "defaultDiscountRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "assumedGrossAssetValue",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "string",
        key: "leasingStructure",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "assumedExitCapRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "assumedExitYear",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
];

export const landlordShareProperties: BuildingPropertyField[] = [
    {
        type: "number",
        key: "defaultLandlordShareCost",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultLandlordShareElectricity",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultLandlordShareNaturalGas",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultLandlordShareSteam",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
];

export const modelWeightProperties: BuildingPropertyField[] = [
    {
        type: "number",
        key: "defaultWeightEmissionSavings",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultWeightNetPresentValue",
        canEdit: false,
        required: false,
        allowNegativeNumber: false,
    },
];

export const utilityProperties: BuildingPropertyField[] = [
    {
        type: "number",
        key: "defaultUtilityInflationRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultElectricityUtilityRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultNaturalGasUtilityRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "defaultSteamUtilityRate",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
];

export const otherProperties: BuildingPropertyField[] = [
    {
        type: "string",
        key: "customerBuildingIdentifier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "string",
        key: "notes",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
];

export const multiplierProperties: BuildingPropertyField[] = [
    {
        type: "number",
        key: "coolingMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "fansMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "heatingMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "lightingMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "plugLoadMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "processMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "pumpsMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "domesticHotWaterMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
    {
        type: "number",
        key: "ventilationRateMultiplier",
        canEdit: true,
        required: false,
        allowNegativeNumber: false,
    },
];

function BuildingAdvancedAttributes(props: BuildingSectionProps) {
    const { hasPermission } = usePermissions();
    return (
        <div>
            <div className="construction-sidebar__body__header" style={{ paddingTop: "var(--size-margin-m)" }}>
                <OverviewIcon />
                <Header size="medium">Advanced Attributes</Header>
            </div>
            <div style={{ marginTop: "25px" }}>
                <h4>Misc Attributes</h4>
                <BuildingSectionBody {...props} fields={otherProperties} />
                <h4>Financial Assumptions and Overrides</h4>
                <BuildingSectionBody {...props} fields={financialProperties} />
                <h4 style={{ display: "none" }}>Default Landlord Share of Utility and Measure Cost</h4>
                <BuildingSectionBody {...props} fields={landlordShareProperties} />
                <h4>Utility Rate Defaults and Inflation Override</h4>
                <BuildingSectionBody {...props} fields={utilityProperties} />
                <div>
                    <h4>Recommendation Algorithm Weight Overrides</h4>
                    <BuildingSectionBody {...props} fields={modelWeightProperties} />
                    {hasPermission(UserPermissions.ConfigureModelingSettings) && (
                        <>
                            <h4>Ideal Air Load Multiplier Overrides (Nuclear Option)</h4>
                            <BuildingSectionBody {...props} fields={multiplierProperties} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BuildingAdvancedAttributes;
